// @flow
import * as React from 'react';
import { Box } from 'grommet';
import { injectIntl, IntlShape } from 'react-intl';
import { withRouter } from 'react-router-dom';
import type { Match, History } from 'react-router-dom';
import { withLeaflet } from 'react-leaflet';
import dataPortMessages from 'esm-web-pro/messages/dataPortMessages';
import UpperContainer from 'esm-web-pro/components/layout/sidePanel/UpperContainer';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import DataPortAttributes from 'esm-web-pro/containers/pages/network/objects/dataport/DataPortAttributes';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import DataPortActions from 'esm-web-pro/containers/pages/network/objects/dataport/DataPortActions';
import DataPortContent from 'esm-web-pro/containers/pages/network/objects/dataport/DataPortContent';
import UseHighlightPoint from 'esm-web-pro/hooks/useHighlightPoint';
import type { LeafletContext } from 'react-leaflet/lib/types';
import DataPortIcon from 'esm-web-pro/components/layout/sidePanel/icons/DataPortIcon';
import { ClickedRowContext } from 'esm-web-pro/containers/pages/network/objects/point12/ClickedRowContext';
import BottomContainer from 'esm-web-pro/components/layout/sidePanel/BottomContainer';
import NavigateBackButton from 'esm-web-pro/components/maps/NavigateBackButton';
import SidePane from 'esm-web-pro/components/layout/SidePane';

type Props = {
    panelId: String,
    object: Object,
    intl: IntlShape,
    map: { is_draft: boolean },
    match: Match,
    history: History,
    updateObject: Function,
    resource: string,
    editor: BaseEditor,
    leaflet: LeafletContext,
}

const DataPort = (props: Props) => {
    const {
        object: dataPort, intl: { formatMessage }, map: networkMap, match,
        updateObject, resource, editor, leaflet, panelId,
    } = props;

    const [clickedRowIdentity, setClickedRowIdentity] = React.useState(null);
    const point11 = dataPort.container.container;
    let number = point11.sequence;
    if (point11.type === 'pop' && point11.alias) {
        number = point11.alias;
    }
    const focusPoint = React.useCallback(
        UseHighlightPoint(point11.coordinates.coordinates, number), [point11],
    );

    const getPointCoords = React.useCallback(() => [...dataPort.container.container.coordinates.coordinates]
        .reverse(),
    [dataPort]);

    const handleView = React.useCallback((e?: SyntheticMouseEvent<HTMLInputElement>,
        callbacks = null) => {
        setClickedRowIdentity(null);
        editor.unregisterAllLoadingCallback();

        if (callbacks) {
            callbacks.forEach((callback) => editor.registerLoadingCallback(callback));
        } else {
            editor.registerLoadingCallback(focusPoint);
            if (leaflet.map) {
                leaflet.map.highlightedGeometry = {
                    bounds: null, center: getPointCoords(),
                };
                leaflet.map.panTo(getPointCoords());
            }
        }

        editor.reloadFeatures();
    }, [focusPoint, editor]);

    React.useEffect(() => {
        handleView(null);

        return (() => {
            editor.unregisterAllLoadingCallback();
        });
    }, [editor, handleView]);

    const navigateBack = () => {
        props.history.push(props.match.url.split('/layer')[0]);
    };


    return (
        <ClickedRowContext.Provider value={[clickedRowIdentity, setClickedRowIdentity]}>
            <SidePane
                id={panelId}
                openInitially
                closeAllOpened
                title={dataPort.data_port_number}
                titleLabel={formatMessage(dataPortMessages.dataport)}
                titleIcon={<DataPortIcon />}
                titleOnView={handleView}
                actions={<NavigateBackButton />}
            >
                <Box height="100%" justify="between">
                    <Box flex>
                        <UpperContainer>
                            <HorizontalLine color="grey" />
                            <DataPortAttributes dataPort={dataPort} url={match.url} onView={handleView} />
                            <HorizontalLine color="grey" />
                            <Box direction="row">
                                <DataPortActions
                                    dataPort={dataPort}
                                    map={networkMap}
                                    updateObject={updateObject}
                                    resource={resource}
                                    onEdit={updateObject}
                                    onView={handleView}
                                    editor={editor}
                                />
                            </Box>
                            <HorizontalLine color="grey" />
                        </UpperContainer>
                        <BottomContainer>
                            <DataPortContent
                                dataPort={dataPort}
                                map={networkMap}
                                editor={editor}
                                updateObject={updateObject}
                                pointCoords={getPointCoords()}
                                handleView={handleView}
                            />
                        </BottomContainer>
                    </Box>
                </Box>
            </SidePane>
        </ClickedRowContext.Provider>
    );
};

export default withLeaflet(withRouter(injectIntl(DataPort)));
